import { useContainer } from '../../useContainer';
import { ClientApi, ToastStateHandler } from '../../../api';
import { UpdateClientDto } from '../../../commonTypes/client.types';
import { dispatch } from '../../../store';
import { editClient, setShowEditClientForm } from '../../../store/slices/clients.slice';
import {
    invalidateActivityFeedCache,
    invalidateActivityLogsCache,
    invalidateProjectMetrics,
    invalidateSingleProjectCache,
    invalidateTimesheetCache,
} from '../../../store/slices/projects.slice';
import { invalidateProjectAssignees } from '../../../store/slices/project-assignees.slice';
import { useTranslation } from '../../useTranslation';

type PatchClientOptions = {
    reason?: string;
    onSuccess?: () => void;
    onError?: () => void;
    affectedProjectIds?: string[];
};

const useHttpPatchClient = () => {
    const { t } = useTranslation('newNotifications');
    const clientApi: ClientApi = useContainer(ClientApi);
    const toastHandler = useContainer(ToastStateHandler);

    const execSubmit = (clientId: string, body: UpdateClientDto, options?: PatchClientOptions | string) => {
        const normalizedOptions: PatchClientOptions = typeof options === 'string' ? { reason: options } : options || {};
        const { reason, onSuccess, onError, affectedProjectIds = [] } = normalizedOptions;
        const { exec, abort } = clientApi.update({
            params: { clientId },
            body,
            onSuccess: _ => {
                dispatch(editClient({ clientId, updateClient: body }));
                affectedProjectIds.forEach(projectId => {
                    dispatch(invalidateSingleProjectCache(projectId));
                    dispatch(invalidateProjectAssignees(projectId));
                    dispatch(invalidateProjectMetrics(projectId));
                    dispatch(invalidateTimesheetCache(projectId));
                    dispatch(invalidateActivityLogsCache(projectId));
                    dispatch(invalidateActivityFeedCache(projectId));
                });
                dispatch(setShowEditClientForm(false));
                if (reason === 'basicInfo') {
                    toastHandler.push('success', t('clients.updateInfoTitle'), t('clients.updateInfoSuccess'));
                    onSuccess?.();
                    return;
                }
                if (reason === 'customFields') {
                    toastHandler.push('success', t('clients.updateFieldTitle'), t('clients.updateFieldSuccess'));
                    onSuccess?.();
                    return;
                }
                toastHandler.push('success', t('clients.updateTitle'), t('clients.updateSuccess'));
                onSuccess?.();
            },
            onError: _ => {
                toastHandler.push('error', t('clients.updateTitle'), t('clients.updateError'));
                onError?.();
            },
        });
        exec();
        return abort;
    };

    return { execSubmit };
};

export default useHttpPatchClient;
